import type {
  CaseListColumnPreference,
  DonorFilter,
  RemoteMemberProfile,
  RemoteUserProfile,
  UserProfile,
  RemoteExternalUser,
  ExternalUser,
  FollowerInvite,
  ChatroomInvite,
  ChatroomMember,
  ChatroomMemberMap,
  MemberProfileV2,
  UserProfileWithSelection,
} from './types';
import type { ApiFollowerInvite, ApiChatroomInvite } from './apiTypes';
import Images from '../Themes/Images';
import sift from 'sift';

const getDonorFilter = (donorFilter: any): DonorFilter => {
  const endResult = {
    status: 'All',
    donorType: 'All',
  };

  if (donorFilter?.status) {
    endResult.status = donorFilter.status;
  }

  if (donorFilter?.donor_type) {
    endResult.donorType = donorFilter.donor_type;
  }

  return endResult;
};

const parseRawProfile = (
  profile: RemoteUserProfile | RemoteMemberProfile,
  verifiedOrgMember: boolean,
  emailUpdate?: string,
): UserProfile => ({
  userId: profile.user_id,
  accessLevel: profile.access_level,
  license: profile.license || 'Standard',
  firstName: profile.first_name,
  lastName: profile.last_name,
  profilePicture: profile.presigned_profile_picture_url,
  profilePictureThumbnail: profile.presigned_profile_picture_thumbnail_url,
  department: profile.department,
  organizationalRole: profile.organizational_role,
  organPrograms: profile.organ_programs,
  email: profile.email,
  emailUpdate,
  isEmailVerified: profile.is_email_verified,
  registrationPrompt: profile.registration_prompt,
  phone: profile.phone,
  organizationId: profile.organization_id || undefined,
  organizationName: profile.organization_name || '',
  verifiedOrgMember,
  templatePreferences: profile.template_preferences || null,
  clientPermissions: profile.client_permissions || null,
  communicationPreferences: profile.communication_preferences,
  chatroomSort: profile.chatroom_sort || 'DEFAULT',
  chatroomFilter: { status: 'All', chatType: 'All' },
  donorSort: profile.donor_sort || 'DEFAULT',
  donorFilter: getDonorFilter(profile.donor_filter),
  globalDoNotDisturbEnd: profile.global_do_not_disturb_end || '',
  globalAlertSound: profile.global_alert_sound || 'Default',
  globalHighPriorityAlertSound: profile.global_high_priority_alert_sound || 'Default',
  emailNotification: profile.email_notification || false,
  smsNotification: profile.sms_notification || false,
  smsSenderNumber: profile.sms_sender_number || '',
  adminNotification: profile.admin_notification || false,
  notificationPreferences: profile.notification_preferences,
  sentCount: profile.sent_count || 0,
  readCount: profile.read_count || 0,
  caseListColumns: profile.case_list_columns as CaseListColumnPreference[] | undefined,
});

const parseProfile = (profile: RemoteUserProfile): UserProfile =>
  parseRawProfile(profile, profile.verified_organization_member, profile.email_update);

export const parseExternalUser = (profile: RemoteExternalUser): ExternalUser => ({
  userId: profile.external_user_id,
  fullName: profile.full_name,
  phone: profile.phone,
  email: profile.email,
  details: profile.details,
  acinData: profile.acin_data,
});

export const parseFollowerInvite = (invite: ApiFollowerInvite): FollowerInvite => ({
  followerInviteId: invite.follower_invite_id,
  followerId: invite.follower_id,
  email: invite.email,
  phone: invite.phone,
  dateInvited: invite.date_invited,
  firstName: invite.first_name,
  lastName: invite.last_name,
  organization: invite.organization,
  role: invite.role,
});

export const parseChatroomInvite = (invite: ApiChatroomInvite): ChatroomInvite => ({
  chatroomInviteId: invite.chatroom_invite_id,
  chatroomId: invite.chatroom_id,
  email: invite.email,
  phone: invite.phone,
  firstName: invite.first_name,
  lastName: invite.last_name,
  organization: invite.organization,
  role: invite.role,
});

// Members only have organization_id if they are verified
export const parseMemberProfile = (profile: RemoteMemberProfile): UserProfile =>
  parseRawProfile(profile, !!profile.organization_id);

export function isChatroomMemberType(profile: UserProfile | ChatroomMember): profile is ChatroomMember {
  return 'membershipStatus' in profile && typeof profile.membershipStatus === 'string';
}

export function isChatroomMemberMap(obj: ChatroomMemberMap | Record<number, boolean>): obj is ChatroomMemberMap {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const keys = Object.keys(obj);

  if (keys.length === 0) {
    return true;
  }
  // Check if key is type string or number to determine type
  return typeof keys[0] === 'string';
}

export function profileToUserProfile(profile: UserProfile | MemberProfileV2): UserProfile {
  // If it's already a UserProfile, just return the profile
  if ('isEmailVerified' in profile) {
    return profile;
  }

  // If it's a MemberProfileV2, create a minimal UserProfile
  return {
    ...profile,
    isEmailVerified: false, // Default value
    registrationPrompt: true, // Default value
    verifiedOrgMember: false, // Default value
    clientPermissions: {}, // Default empty object
    chatroomSort: 'DEFAULT', // Default value, adjust based on your needs
    chatroomFilter: {
      status: 'All',
      chatType: 'All',
    }, // Default value, adjust based on your needs
    donorSort: 'DEFAULT', // Default value, adjust based on your needs
    donorFilter: {
      status: 'All',
      donorType: 'All',
    }, // Default value, adjust based on your needs
    globalDoNotDisturbEnd: '', // Default empty string
    globalAlertSound: 'default', // Default value
    globalHighPriorityAlertSound: 'default', // Default value
    emailNotification: true, // Default value
    smsNotification: false, // Default value
    smsSenderNumber: '', // Default empty string
    adminNotification: false, // Default value
    organizationName: profile.organizationName || '', // Use provided or empty string
    templatePreferences: undefined, // Default empty object
  };
}

export function profileToUserProfileWithSelection(profile: UserProfile | MemberProfileV2): UserProfileWithSelection {
  return {
    ...profileToUserProfile(profile),
    isSelected: true,
  };
}

export function getProfilePicture(user: UserProfile | MemberProfileV2 | undefined) {
  return user?.profilePicture || Images.circledDefaultProfile;
}

export function getProfilePictureThumbnail(user: UserProfile | MemberProfileV2 | undefined) {
  return user?.profilePictureThumbnail || Images.circledDefaultProfile;
}

/**
 * This method returns a function that will determine if a profile matches the search query. This can
 * be used as a predicate to filter an array of profiles or individual check if a profile matches
 *
 * @param query the query string to search for
 * @returns a function that takes a profile and returns true if the profile matches the query
 */
export function createProfileQueryFilter(query: string) {
  if (!query) {
    return () => true;
  }

  const upperQuery = query.toUpperCase();

  return sift<MemberProfileV2>({
    $where() {
      const nameMatch =
        this.firstName && this.lastName
          ? `${this.firstName} ${this.lastName}`.toUpperCase().includes(upperQuery)
          : false;
      const emailMatch = this.email ? this.email.toUpperCase().includes(upperQuery) : false;
      const phoneMatch = this.phone ? this.phone.includes(query) : false;

      return nameMatch || emailMatch || phoneMatch;
    },
  });
}

/**
 * This method returns a sorted array of profiles that match the search query. If showSelf is true, the
 * profile with the matching memberId will be moved to the front of the array.
 *
 * @param profiles the MemberProfile objects to filter
 * @param query the query string to match on
 * @param showSelf whether the caller should be returned in the list
 * @param memberId the memberId of the caller
 * @returns a sorted array of profiles that match the search query
 */
export function getFilteredAndSortedProfiles(
  profiles: MemberProfileV2[],
  query: string,
  showSelf: boolean,
  memberId: string | number,
): MemberProfileV2[] {
  const filtered = query ? profiles.filter(createProfileQueryFilter(query)) : profiles;

  if (!showSelf) {
    return filtered;
  }

  return [...filtered].sort((a: MemberProfileV2, b: MemberProfileV2) => {
    if (a.userId === memberId) return -1;
    if (b.userId === memberId) return 1;
    return 0;
  });
}

export default parseProfile;
